import { forwardRef, useRef, useState, useCallback, useImperativeHandle } from 'react';

import MultiLineInput from 'components/MultiLineInput';

import AutocompleteDropdown from './AutocompleteDropdown';

interface AutocompleteInputProps {
    resourceType: 'task' | 'milestone',
    projectId?: string,
    autofocus?: boolean,
    autofocusDelay?: number,
    defaultValue?: string,
    placeholder?: string,
    excludedResources?: string[],
    onChange?: (value: string, event: React.ChangeEvent) => void,
    onCreate?: (value: string) => void,
    onBlur?: (value: string, event: React.FocusEvent) => void,
    onFocus?: (event: React.FocusEvent) => void,
    onKeyDown?: (event: React.KeyboardEvent) => void,
    onPaste?: (event: ClipboardEvent, value: string) => void,
    onSelect?: (resource: any) => void,
    refElement: HTMLElement | null
}

const AutocompleteInput = forwardRef<any, AutocompleteInputProps>(({ resourceType, projectId, autofocus, autofocusDelay, defaultValue, placeholder, excludedResources, onBlur, onFocus, onChange, onCreate, onKeyDown, onPaste, onSelect, refElement }: AutocompleteInputProps, ref) => {
    const [focused, setFocused] = useState<boolean>(false);
    const [value, setValue] = useState<string>(defaultValue || '');
    const inputCtrl = useRef<any>(null);
    const autocompleteCtrl = useRef<any>(null);

    const handleFocus = useCallback((event: React.FocusEvent) => {
        setFocused(true);
        onFocus?.(event);
    }, [onFocus]);

    const handleBlur = useCallback((value: string, event: React.FocusEvent) => {
        setFocused(false);
        onBlur?.(value, event);
    }, [onBlur]);

    const handleChange = useCallback((value: string, event: React.ChangeEvent) => {
        setValue(value);
        onChange?.(value, event);
    }, [onChange]);

    const handleSelect = useCallback(async (resource: any) => {
        if (resource.id === 'create') {
            // create
            onCreate?.(value);
        } else {
            // select
            onSelect?.(resource)
        }

        inputCtrl.current?.reset();
        setValue('');
    }, [value, onSelect, onCreate]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (onSelect) {
            switch (event.which) {
                case 40: // down
                    autocompleteCtrl.current?.selectNext();
                    event.preventDefault();
                    break;

                case 38: // up
                    autocompleteCtrl.current?.selectPrev();
                    event.preventDefault();
                    break;

                case 13: // enter
                    handleSelect(autocompleteCtrl.current?.getSelected());
                    event.preventDefault();
                    break;
            }
        }

        onKeyDown?.(event);
    }, [autocompleteCtrl, handleSelect, onSelect, onKeyDown]);

    useImperativeHandle(ref, () => {
       return inputCtrl.current;
    });

    return (
        <>
            <MultiLineInput
                className="item-title"
                canEdit={true}
                autoFocus={autofocus}
                autoFocusDelay={autofocusDelay}
                value={defaultValue}
                placeholder={placeholder}
                ref={inputCtrl}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={onPaste}
            />
            
            {
                onSelect && value && focused ? (
                    <AutocompleteDropdown
                        projectId={projectId}
                        resourceType={resourceType}
                        excludedResources={excludedResources}
                        term={value}
                        onSelect={handleSelect}
                        refElement={refElement}
                        provideController={autocompleteCtrl}
                    />
                ) : null
            }
        </>
    )
});

export default AutocompleteInput;
